import {useCallback, useState} from 'react'
import {View} from 'react-native'
import {ComAtprotoServerCreateSession} from '@atproto/api'
import {msg} from '@lingui/core/macro'
import {useLingui} from '@lingui/react'
import {Trans} from '@lingui/react/macro'

import {useAgent, useSession} from '#/state/session'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import * as Dialog from '#/components/Dialog'
import {
  isValidCode,
  TokenField,
} from '#/components/dialogs/EmailDialog/components/TokenField'
import * as TextField from '#/components/forms/TextField'
import {Lock_Stroke2_Corner0_Rounded as Lock} from '#/components/icons/Lock'
import {Loader} from '#/components/Loader'
import {Text} from '#/components/Typography'

/**
 * Password gate for sensitive actions (e.g. sharing a family invite). Verifies
 * the account password against the PDS via `com.atproto.server.createSession`
 * and, when the account has email 2FA enabled, adds a confirmation-code step
 * (same TokenField pattern as the EmailDialog).
 */

// Single instance per screen tree — the hook writes the callback, the dialog
// component reads it when verification succeeds.
const onVerifiedRef: {current: (() => void) | null} = {current: null}

export function usePasswordConfirmDialog() {
  const control = Dialog.useDialogControl()
  const open = useCallback(
    (onVerified: () => void) => {
      onVerifiedRef.current = onVerified
      control.open()
    },
    [control],
  )
  return {control, open}
}

export function PasswordConfirmDialog({
  control,
  description,
}: {
  control: Dialog.DialogControlProps
  /** Override the default "share this family invitation" copy. */
  description?: string
}) {
  const {_} = useLingui()
  const t = useTheme()
  const agent = useAgent()
  const {currentAccount} = useSession()

  const [password, setPassword] = useState('')
  const [token, setToken] = useState('')
  const [needsToken, setNeedsToken] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const verify = useCallback(async () => {
    const identifier = currentAccount?.handle || currentAccount?.did
    if (!identifier || !password) return
    setIsProcessing(true)
    setError(null)
    try {
      await agent.com.atproto.server.createSession({
        identifier,
        password,
        ...(needsToken ? {authFactorToken: token.trim()} : {}),
      })
      const cb = onVerifiedRef.current
      onVerifiedRef.current = null
      setPassword('')
      setToken('')
      setNeedsToken(false)
      control.close(() => cb?.())
    } catch (e: unknown) {
      if (
        e instanceof ComAtprotoServerCreateSession.AuthFactorTokenRequiredError
      ) {
        setNeedsToken(true)
        setError(_(msg`Enter the confirmation code from your email.`))
      } else if (
        (e as {message?: string}).message?.includes('Token is invalid')
      ) {
        setError(_(msg`Invalid confirmation code.`))
      } else {
        setError(_(msg`Incorrect password. Please try again.`))
      }
    } finally {
      setIsProcessing(false)
    }
  }, [agent, currentAccount, password, needsToken, token, _, control])

  return (
    <Dialog.Outer control={control}>
      <Dialog.Handle />
      <Dialog.ScrollableInner label={_(msg`Confirm your password`)}>
        <View style={[a.gap_lg]}>
          <View style={[a.gap_xs]}>
            <Text style={[a.text_2xl, a.font_bold]}>
              <Trans>Confirm your password</Trans>
            </Text>
            <Text style={[a.text_md, t.atoms.text_contrast_medium]}>
              {description ? (
                description
              ) : (
                <Trans>
                  Enter your password to confirm this action.
                </Trans>
              )}
            </Text>
          </View>

          {needsToken ? (
            <View style={[a.gap_sm]}>
              <TextField.LabelText>
                <Trans>Confirmation code</Trans>
              </TextField.LabelText>
              <TokenField
                value={token}
                onChangeText={setToken}
                onSubmitEditing={verify}
              />
            </View>
          ) : (
            <TextField.Root>
              <TextField.Icon icon={Lock} />
              <TextField.Input
                label={_(msg`Password`)}
                autoCapitalize="none"
                autoCorrect={false}
                secureTextEntry={true}
                autoComplete="off"
                value={password}
                onChangeText={setPassword}
                onSubmitEditing={verify}
              />
            </TextField.Root>
          )}

          {error ? (
            <Text style={[{color: t.palette.negative_400}, a.text_sm]}>
              {error}
            </Text>
          ) : null}

          <View style={[a.flex_row, a.gap_sm, a.justify_end]}>
            <Button
              label={_(msg`Cancel`)}
              size="small"
              color="secondary"
              onPress={() => control.close()}>
              <ButtonText>
                <Trans>Cancel</Trans>
              </ButtonText>
            </Button>
            <Button
              label={_(msg`Verify`)}
              size="small"
              color="primary"
              disabled={
                isProcessing || !password || (needsToken && !isValidCode(token))
              }
              onPress={verify}>
              <ButtonText>
                {isProcessing ? (
                  <Trans>Verifying…</Trans>
                ) : (
                  <Trans>Verify</Trans>
                )}
              </ButtonText>
              {isProcessing && <Loader size="xs" />}
            </Button>
          </View>
        </View>
        <Dialog.Close />
      </Dialog.ScrollableInner>
    </Dialog.Outer>
  )
}
